import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Drawer, List, Divider, Typography } from 'antd';
import {
  LogOut,
  Home,
  Folder,
  User,
  Menu,
  X,
  Sun,
  Moon,
} from 'lucide-react';
import Icon from './Icon';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useAuth } from '../auth/auth.context';
import ColorModeToggle from '../theme/ColorModeToggle';
import { useIsMobile } from '../hooks/useBreakpoint';
import { useThemeMode } from '../theme/ThemeProvider';
import { getAirsmartLogo } from '../theme/brandingConfig';
import MyFilesDialog from './MyFilesDialog';
import UserRoleDialog from './UserRoleDialog';

const Header = ({ showHomeButton = true, hideUserActions = false }) => {
  const navigate = useNavigate();
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';
  const headerLogo = getAirsmartLogo(mode);
  const isMobile = useIsMobile();
  const { currentUser, userProfile } = useAuth(); // Lấy userProfile từ context
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [userRoleDialogOpen, setUserRoleDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Use profile data from context
  const userRole = userProfile?.role || 'installer';
  const userDisplayName = userProfile?.displayName || 'User';

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleMyFilesClick = () => {
    setFilesDialogOpen(true);
  };

  const handleUserRoleClick = () => {
    setUserRoleDialogOpen(true);
  };

  const handleRoleUpdated = () => {
    // Không cần update global cache nữa
    // Nếu muốn update UI ngay lập tức, có thể cần refresh lại page hoặc update context
    // Tuy nhiên, context thường sẽ được sync nếu initializeUser được gọi lại
    // Ở đây ta cứ để UI tự update khi user reload hoặc navigate

    // Tạm thời nếu thay đổi role thủ công (dev mode?), ta có thể force update window location
    // hoặc notify user
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileMenuItemClick = (action) => {
    handleMobileMenuClose();
    action();
  };

  const headerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
    backgroundColor: isDark ? '#141414' : '#ffffff',
    color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
    boxShadow: isDark
      ? 'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px'
      : 'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  };

  const toolbarStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 24,
    paddingLeft: 16,
    minHeight: 64,
  };

  const menuItems = [
    {
      key: 'user',
      icon: <Icon as={User} />,
      label: userDisplayName,
      description: userRole.charAt(0).toUpperCase() + userRole.slice(1),
      onClick: () => handleMobileMenuItemClick(handleUserRoleClick),
    },
    {
      key: 'files',
      icon: <Icon as={Folder} />,
      label: 'My Files',
      onClick: () => handleMobileMenuItemClick(handleMyFilesClick),
    },
  ];

  return (
    <header style={headerStyle}>
      <div style={toolbarStyle}>
        <div 
          style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }} 
          onClick={handleHomeClick}
        >
          <img src={headerLogo} alt="AirSmart" style={{ height: isMobile ? 64 : 64, width: 'auto' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Desktop Menu */}
          {!isMobile && (
            <>
              {showHomeButton && (
                <Button
                  type="text"
                  shape="circle"
                  icon={<Icon as={Home} />}
                  onClick={handleHomeClick}
                  aria-label="home"
                />
              )}
              {!hideUserActions && currentUser && (
                <>
                  <Button
                    type="text"
                    icon={<Icon as={User} />}
                    onClick={handleUserRoleClick}
                  >
                    {userDisplayName}
                  </Button>
                  <Button
                    type="text"
                    icon={<Icon as={Folder} />}
                    onClick={handleMyFilesClick}
                  >
                    My Files
                  </Button>
                  <Button
                    type="text"
                    shape="circle"
                    icon={<Icon as={LogOut} />}
                    onClick={handleLogout}
                    aria-label="logout"
                  />
                </>
              )}
              <ColorModeToggle style={{ marginLeft: 16 }} />
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <>
              {showHomeButton && (
                <Button
                  type="text"
                  shape="circle"
                  icon={<Icon as={Home} />}
                  onClick={handleHomeClick}
                  aria-label="home"
                />
              )}
              {!hideUserActions && currentUser && (
                <Button
                  type="text"
                  shape="circle"
                  icon={<Icon as={Menu} />}
                  onClick={handleMobileMenuToggle}
                  aria-label="menu"
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* My Files Dialog */}
      <MyFilesDialog
        open={filesDialogOpen}
        onClose={() => setFilesDialogOpen(false)}
        userRole={userRole}
      />

      {/* User Role Dialog */}
      <UserRoleDialog
        open={userRoleDialogOpen}
        onClose={() => setUserRoleDialogOpen(false)}
        onRoleUpdated={handleRoleUpdated}
      />

      {/* Mobile Menu Drawer */}
      <Drawer
        placement="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        width={280}
        zIndex={1400}
        title={null}
        closable={false}
      >
        <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 'bold' }}>
            Menu
          </Typography.Title>
          <Button
            type="text"
            shape="circle"
            icon={<Icon as={X} />}
            onClick={handleMobileMenuClose}
          />
        </div>
        <Divider style={{ margin: 0 }} />
        <List
          dataSource={menuItems}
          renderItem={(item) => (
            <List.Item
              key={item.key}
              onClick={item.onClick}
              style={{ cursor: 'pointer', padding: '12px 16px' }}
            >
              <List.Item.Meta
                avatar={item.icon}
                title={item.label}
                description={item.description}
              />
            </List.Item>
          )}
        />
        <Divider style={{ margin: '4px 0' }} />
        <List>
          <List.Item
            onClick={toggleMode}
            style={{ cursor: 'pointer', padding: '12px 16px' }}
          >
            <List.Item.Meta
              avatar={mode === 'dark' ? <Icon as={Sun} /> : <Icon as={Moon} />}
              title="Theme"
              description={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            />
          </List.Item>
        </List>
        <Divider style={{ margin: '4px 0' }} />
        <List>
          <List.Item
            onClick={() => handleMobileMenuItemClick(handleLogout)}
            style={{ cursor: 'pointer', padding: '12px 16px' }}
          >
            <List.Item.Meta
              avatar={<Icon as={LogOut} />}
              title="Logout"
            />
          </List.Item>
        </List>
      </Drawer>
    </header>
  );
};

export default Header;
